/**
 * TikTok clips, opened in a lightbox.
 *
 * The tiles are plain images until someone taps one. Then the clip plays in the same
 * GLightbox modal the review videos use, inside TikTok's *player* embed - the bare video
 * with its own controls - rather than TikTok's post embed. The post embed is a whole
 * TikTok card (author, caption, music, buttons, ~325px wide and ~700px tall) and dropping
 * it into a 9:16 tile broke the grid: measured, the tile collapsed to a 268x150 black box
 * in its top-left corner. In a modal the grid never moves, and the clip gets a size you
 * can actually watch it at, which a 2-up tile on a phone never was.
 *
 * All the section's clips open as one set, so a visitor can swipe or arrow from one to
 * the next. Only the clip on screen has a live iframe: moving on blanks the one being
 * left, so two clips never play over each other and nothing loads that is not watched.
 *
 * Nothing from TikTok or the lightbox loads until the first tap. If the lightbox cannot
 * load, the tap opens the clip on TikTok instead, so the section still works when the
 * network is hostile to it.
 */
const PLAYER_PARAMS = new URLSearchParams({
  autoplay: '1',
  controls: '1',
  progress_bar: '1',
  play_button: '1',
  volume_control: '1',
  fullscreen_button: '1',
  timestamp: '0',
  loop: '0',
  music_info: '0',
  description: '0',
  rel: '0',
  native_context_menu: '0',
  closed_caption: '0',
}).toString();

/** TikTok video URLs end in the numeric id: /@handle/video/1234567890 */
function videoIdFrom(url) {
  const match = String(url).match(/\/video\/(\d+)/);
  return match ? match[1] : null;
}

function playerUrl(id) {
  return `https://www.tiktok.com/player/v1/${id}?${PLAYER_PARAMS}`;
}

function openOnTikTok(url) {
  window.open(url, '_blank', 'noopener');
}

/** The same loader as the review videos, so the library is fetched once per page. */
function loadLightbox() {
  if (window.GLightbox) return Promise.resolve(window.GLightbox);
  window.__beeulLightbox ||= new Promise((resolve) => {
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://cdn.jsdelivr.net/npm/glightbox@3.3.1/dist/css/glightbox.min.css';
    document.head.appendChild(css);

    const js = document.createElement('script');
    js.src = 'https://cdn.jsdelivr.net/npm/glightbox@3.3.1/dist/js/glightbox.min.js';
    js.onload = () => resolve(window.GLightbox || null);
    js.onerror = () => resolve(null);
    document.head.appendChild(js);
  });
  return window.__beeulLightbox;
}

/**
 * Each slide is a 9:16 frame holding an iframe whose URL waits in data-src until that
 * slide is the one on screen.
 */
function slideFor(id, label) {
  const title = String(label || 'TikTok clip').replace(/"/g, '&quot;');
  return {
    content: `<div class="beeul-tok-player"><iframe data-src="${playerUrl(id)}" title="${title}" allow="autoplay; fullscreen; encrypted-media; picture-in-picture" allowfullscreen></iframe></div>`,
    width: 'auto',
    height: 'auto',
  };
}

/**
 * Gives the slide on screen its player and blanks every other one. Removing the src is
 * what actually stops a clip; pausing a cross-origin player is not something the page is
 * allowed to do.
 */
function syncPlayers(container) {
  // A closing modal keeps its current slide through the fade-out; without this the
  // class changes of that fade would put the player straight back.
  if (container.dataset.beeulClosing) return;
  container.querySelectorAll('.beeul-tok-player iframe').forEach((frame) => {
    const onScreen = frame.closest('.gslide')?.classList.contains('current');
    if (onScreen && !frame.getAttribute('src')) frame.src = frame.dataset.src;
    if (!onScreen && frame.getAttribute('src')) frame.removeAttribute('src');
  });
}

function stopAll() {
  document.querySelectorAll('.glightbox-container').forEach((container) => {
    container.dataset.beeulClosing = 'true';
  });
  document.querySelectorAll('.beeul-tok-player iframe[src]').forEach((frame) => frame.removeAttribute('src'));
}

/*
 * Sound stops the moment a close is asked for - the close button, a tap on the backdrop
 * or Esc - rather than once GLightbox's fade-out has finished and it fires its own close
 * event. Caught here in the capture phase because GLightbox binds its buttons to its own
 * internal close, which cannot be wrapped from outside.
 */
document.addEventListener(
  'click',
  (event) => {
    // Anywhere in the modal but the player and the arrows closes it (GLightbox's
    // closeOnOutsideClick), and so does the close button.
    const inModal = event.target.closest('.glightbox-container');
    if (inModal && !event.target.closest('.beeul-tok-player, .gnext, .gprev')) stopAll();
  },
  true
);

document.addEventListener(
  'keydown',
  (event) => {
    if (event.key === 'Escape' && document.documentElement.classList.contains('glightbox-open')) stopAll();
  },
  true
);

document.addEventListener('click', async (event) => {
  const tile = event.target.closest('.beeul-tok__tile[data-tiktok]');
  if (!tile) return;

  const grid = tile.closest('.beeul-tok__grid');
  const tiles = grid ? [...grid.querySelectorAll('.beeul-tok__tile[data-tiktok]')] : [tile];
  const clips = tiles
    .map((node) => ({ node, url: node.dataset.tiktok, id: videoIdFrom(node.dataset.tiktok) }))
    .filter((clip) => clip.id);

  const index = clips.findIndex((clip) => clip.node === tile);
  if (index === -1) {
    openOnTikTok(tile.dataset.tiktok);
    return;
  }

  tile.setAttribute('aria-busy', 'true');
  const lightbox = await loadLightbox();
  tile.removeAttribute('aria-busy');

  if (!lightbox) {
    openOnTikTok(clips[index].url);
    return;
  }

  const box = lightbox({
    elements: clips.map((clip) => slideFor(clip.id, clip.node.getAttribute('aria-label'))),
    startAt: index,
    loop: false,
    touchNavigation: true,
    keyboardNavigation: true,
    zoomable: false,
    draggable: true,
    openEffect: 'fade',
    closeEffect: 'fade',
    slideEffect: 'slide',
  });

  /*
   * Players follow the slide GLightbox marks as current, watched directly rather than
   * through its slide_changed and close events. Those fire when the slide and fade
   * animations finish, and measured, a change that happens without the animation running
   * left the old clip playing and the new one blank.
   */
  let watcher = null;
  box.on('open', () => {
    const containers = document.querySelectorAll('.glightbox-container');
    const container = containers[containers.length - 1];
    if (!container) return;
    delete container.dataset.beeulClosing;
    syncPlayers(container);
    watcher = new MutationObserver(() => {
      if (!document.documentElement.classList.contains('glightbox-open')) {
        stopAll();
        watcher?.disconnect();
        return;
      }
      syncPlayers(container);
    });
    watcher.observe(container, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    watcher.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  });

  box.on('slide_after_load', () => {
    const containers = document.querySelectorAll('.glightbox-container');
    const container = containers[containers.length - 1];
    if (container) syncPlayers(container);
  });

  box.on('close', () => {
    stopAll();
    watcher?.disconnect();
    tile.focus({ preventScroll: true });
  });

  box.open();
});
